"use client";

import { c as _c } from "react/compiler-runtime";
import { useT, useGameLocale } from "@/lib/i18n";
import { useState, useEffect } from "react";
import { useParams } from "next/navigation";
import { Link } from "@/i18n/navigation";
import { useBetaPrefix } from "@/lib/api/prefix.client";
import { useAuth } from "@/app/contexts/AuthContext";
import { useToast } from "@/app/components/Toast";
import { authHeaders } from "@/app/[locale]/admin/shared";
import { cachedFetch } from "@/lib/fetch-cache";
import { useTryGameTranslations } from "@/lib/game-i18n";
import type { RawRun, Run } from "@/lib/api/run/types";
import { cleanRun } from "@/lib/api/run/util";
import RunSummary, { type PotionInfo } from "./RunSummary";
import SimilarRuns from "./SimilarRuns";
import { CardPill, RelicPill, displayName, type CardInfo, type RelicInfo } from "./RunPills";
import { characterTitle, eventChoiceName, restChoiceName, roomTitle, roomTypeTitle, typedRoom } from "./run-names";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
const CHAR_CSS_VAR: Record<string, string> = {
  IRONCLAD: "var(--color-ironclad)",
  SILENT: "var(--color-silent)",
  DEFECT: "var(--color-defect)",
  NECROBINDER: "var(--color-necrobinder)",
  REGENT: "var(--color-regent)"
};
const ROOM_COLORS: Record<string, string> = {
  monster: "var(--text-secondary)",
  elite: "var(--accent-gold)",
  boss: "var(--color-ironclad)",
  rest: "var(--color-silent)",
  rest_site: "var(--color-silent)",
  shop: "var(--accent-teal)",
  event: "var(--color-necrobinder)",
  treasure: "var(--accent-gold)"
};

// `initialRun` is the server-fetched run from page.tsx. With it, the whole
// page server-renders with real data — before, the body was an empty shell
// until the client refetched the same endpoint, so every run page looked
// identical to crawlers (duplicate content) and carried no unique text.
export default function SharedRunClient(t0) {
  const $ = _c(200);
  const {
    initialRun
  } = t0;
  const {
    hash
  } = useParams();
  const bp = useBetaPrefix();
  const lang = useGameLocale();
  const t = useT();
  const gt = useTryGameTranslations();
  const {
    user
  } = useAuth();
  const {
    toast
  } = useToast();
  const [run, setRun] = useState(initialRun ?? null);
  const [loading, setLoading] = useState(!initialRun);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportEmail, setReportEmail] = useState("");
  const [reportReason, setReportReason] = useState("");
  const [reportState, setReportState] = useState("idle");
  const [reportError, setReportError] = useState(null);
  const [unhiding, setUnhiding] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [cardData, setCardData] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [relicData, setRelicData] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [potionData, setPotionData] = useState(t3);
  const [showDetails, setShowDetails] = useState(false);
  let t4;
  if ($[3] !== hash || $[4] !== initialRun || $[5] !== lang) {
    t4 = () => {
      if (!hash) {
        return;
      }
      if (!initialRun) {
        fetch(`${API}/api/runs/shared/${hash}`).then(_temp).then(raw => setRun(cleanRun(raw))).catch(() => setNotFound(true)).finally(() => setLoading(false));
      }
      cachedFetch(`${API}/api/cards?lang=${lang}`).then(cards => {
        const m = {};
        for (const c of cards) {
          m[c.id] = c;
        }
        setCardData(m);
      });
      cachedFetch(`${API}/api/relics?lang=${lang}`).then(relics => {
        const m_0 = {};
        for (const r_0 of relics) {
          m_0[r_0.id] = r_0;
        }
        setRelicData(m_0);
      });
      cachedFetch(`${API}/api/potions?lang=${lang}`).then(potions => {
        const m_1 = {};
        for (const p of potions) {
          m_1[p.id] = p;
        }
        setPotionData(m_1);
      });
    };
    $[3] = hash;
    $[4] = initialRun;
    $[5] = lang;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== hash || $[8] !== lang) {
    t5 = [hash, lang];
    $[7] = hash;
    $[8] = lang;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  if ($[10] !== lang || $[11] !== run?.is_beta) {
    t6 = () => {
      if (!run?.is_beta) {
        return;
      }
      let cancelled = false;
      cachedFetch(`${API}/api/cards?lang=${lang}&channel=beta`).then(cards_0 => {
        if (cancelled) {
          return;
        }
        setCardData(prev => {
          const m_2 = {
            ...prev
          };
          for (const c_0 of cards_0) {
            if (!(c_0.id in m_2)) {
              m_2[c_0.id] = c_0;
            }
          }
          return m_2;
        });
      });
      cachedFetch(`${API}/api/relics?lang=${lang}&channel=beta`).then(relics_0 => {
        if (cancelled) {
          return;
        }
        setRelicData(prev_0 => {
          const m_3 = {
            ...prev_0
          };
          for (const r_1 of relics_0) {
            if (!(r_1.id in m_3)) {
              m_3[r_1.id] = r_1;
            }
          }
          return m_3;
        });
      });
      cachedFetch(`${API}/api/potions?lang=${lang}&channel=beta`).then(potions_0 => {
        if (cancelled) {
          return;
        }
        setPotionData(prev_1 => {
          const m_4 = {
            ...prev_1
          };
          for (const p_0 of potions_0) {
            if (!(p_0.id in m_4)) {
              m_4[p_0.id] = p_0;
            }
          }
          return m_4;
        });
      });
      return () => {
        cancelled = true;
      };
    };
    $[10] = lang;
    $[11] = run?.is_beta;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = run?.is_beta;
  let t8;
  if ($[13] !== lang || $[14] !== t7) {
    t8 = [t7, lang];
    $[13] = lang;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  useEffect(t6, t8);
  let t9;
  if ($[16] !== cardData || $[17] !== gt) {
    t9 = id => gt(`cards.${id}.title`) ?? cardData[id]?.name ?? displayName(`CARD.${id}`);
    $[16] = cardData;
    $[17] = gt;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const cardTitle = t9;
  let t10;
  if ($[19] !== gt || $[20] !== relicData) {
    t10 = id_0 => gt(`relics.${id_0}.title`) ?? relicData[id_0]?.name ?? displayName(`RELIC.${id_0}`);
    $[19] = gt;
    $[20] = relicData;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const relicTitle = t10;
  let t11;
  if ($[22] !== gt) {
    t11 = id_1 => gt(`encounters.${id_1}.title`) ?? displayName(`ENCOUNTER.${id_1}`);
    $[22] = gt;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const encounterTitle = t11;
  let t12;
  if ($[24] !== gt) {
    t12 = id_2 => gt(`events.${id_2}.title`) ?? displayName(`EVENT.${id_2}`);
    $[24] = gt;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const eventTitle = t12;
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = function copyLink() {
      navigator.clipboard.writeText(window.location.href).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    };
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const copyLink = t13;
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== reportEmail) {
    t15 = t14.test(reportEmail.trim());
    $[28] = reportEmail;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const emailOk = t15;
  let t16;
  if ($[30] !== emailOk || $[31] !== hash || $[32] !== reportEmail || $[33] !== reportReason) {
    t16 = function submitReport() {
      if (!emailOk || !reportReason.trim()) {
        return;
      }
      setReportState("sending");
      setReportError(null);
      fetch(`${API}/api/feedback/run-report`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          run_hash: hash,
          email: reportEmail.trim(),
          reason: reportReason.trim()
        })
      }).then(async r_2 => {
        if (!r_2.ok) {
          const body = await r_2.json().catch(_temp2);
          throw new Error(body?.detail || `${r_2.status}`);
        }
        setReportState("sent");
        setTimeout(() => {
          setShowReport(false);
          setReportState("idle");
          setReportReason("");
          setReportEmail("");
        }, 1800);
      }).catch(e => {
        setReportState("error");
        setReportError(String((e as Error)?.message || e));
      });
    };
    $[30] = emailOk;
    $[31] = hash;
    $[32] = reportEmail;
    $[33] = reportReason;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const submitReport = t16;
  let t17;
  if ($[35] !== hash || $[36] !== run || $[37] !== t || $[38] !== toast) {
    t17 = function setHidden(hidden) {
      if (!run) {
        return;
      }
      setUnhiding(true);
      fetch(`${API}/api/runs/${hash}/hidden`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders()
        },
        body: JSON.stringify({
          hidden
        })
      }).then(r_3 => {
        if (!r_3.ok) {
          throw new Error();
        }
        setRun({
          ...run,
          hidden
        });
        toast(hidden ? t("Run hidden") : t("Run unhidden"), "success");
      }).catch(() => toast(t("Failed to update this run"), "error")).finally(() => setUnhiding(false));
    };
    $[35] = hash;
    $[36] = run;
    $[37] = t;
    $[38] = toast;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const setHidden = t17;
  if (loading) {
    let t18;
    if ($[40] !== t) {
      t18 = t("Loading...");
      $[40] = t;
      $[41] = t18;
    } else {
      t18 = $[41];
    }
    let t19;
    if ($[42] !== t18) {
      t19 = <div className="max-w-4xl mx-auto px-4 py-12 text-center text-[var(--text-muted)]">{t18}</div>;
      $[42] = t18;
      $[43] = t19;
    } else {
      t19 = $[43];
    }
    return t19;
  }
  if (notFound || !run) {
    let t18;
    if ($[44] !== t) {
      t18 = t("Run not found.");
      $[44] = t;
      $[45] = t18;
    } else {
      t18 = $[45];
    }
    let t19;
    if ($[46] !== t18) {
      t19 = <p className="text-[var(--text-muted)] mb-4">{t18}</p>;
      $[46] = t18;
      $[47] = t19;
    } else {
      t19 = $[47];
    }
    const t20 = `${bp}/leaderboards`;
    let t21;
    if ($[48] !== t) {
      t21 = t("Back to");
      $[48] = t;
      $[49] = t21;
    } else {
      t21 = $[49];
    }
    let t22;
    if ($[50] !== t20 || $[51] !== t21) {
      t22 = <Link href={t20} className="text-[var(--accent-gold)] hover:underline">← {t21}</Link>;
      $[50] = t20;
      $[51] = t21;
      $[52] = t22;
    } else {
      t22 = $[52];
    }
    let t23;
    if ($[53] !== t19 || $[54] !== t22) {
      t23 = <div className="max-w-4xl mx-auto px-4 py-12 text-center">{t19}{t22}</div>;
      $[53] = t19;
      $[54] = t22;
      $[55] = t23;
    } else {
      t23 = $[55];
    }
    return t23;
  }
  const player = run.players[run.player_index ?? 0] ?? run.players[0];
  const charId = player.character;
  const charColor = CHAR_CSS_VAR[charId.toUpperCase()] || "var(--accent-gold)";
  let t18;
  if ($[56] !== charId) {
    t18 = charId.toUpperCase();
    $[56] = charId;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  const charLinked = t18 in CHAR_CSS_VAR;
  let t19;
  if ($[58] !== charId || $[59] !== gt) {
    t19 = characterTitle(charId, gt);
    $[58] = charId;
    $[59] = gt;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  const charName = t19;
  let t20;
  if ($[61] !== bp || $[62] !== encounterTitle || $[63] !== eventTitle || $[64] !== run.killed_by_encounter || $[65] !== run.killed_by_event || $[66] !== run.was_abandoned || $[67] !== run.win) {
    t20 = !run.win && !run.was_abandoned ? run.killed_by_encounter ? {
      href: `${bp}/encounters/${run.killed_by_encounter.toLowerCase()}`,
      name: encounterTitle(run.killed_by_encounter)
    } : run.killed_by_event ? {
      href: `${bp}/events/${run.killed_by_event.toLowerCase()}`,
      name: eventTitle(run.killed_by_event)
    } : undefined : undefined;
    $[61] = bp;
    $[62] = encounterTitle;
    $[63] = eventTitle;
    $[64] = run.killed_by_encounter;
    $[65] = run.killed_by_event;
    $[66] = run.was_abandoned;
    $[67] = run.win;
    $[68] = t20;
  } else {
    t20 = $[68];
  }
  const killedBy = t20;
  let t21;
  if ($[69] !== bp || $[70] !== cardData || $[71] !== cardTitle || $[72] !== charColor || $[73] !== charId || $[74] !== charLinked || $[75] !== charName || $[76] !== copied || $[77] !== emailOk || $[78] !== gt || $[79] !== hash || $[80] !== killedBy || $[81] !== player || $[82] !== potionData || $[83] !== relicData || $[84] !== relicTitle || $[85] !== reportEmail || $[86] !== reportError || $[87] !== reportReason || $[88] !== reportState || $[89] !== run || $[90] !== setHidden || $[91] !== showDetails || $[92] !== showReport || $[93] !== submitReport || $[94] !== t || $[95] !== unhiding || $[96] !== user?.is_admin) {
    const sortedDeck = [...player.deck].sort(_temp3);
    const t22 = `${bp}/leaderboards`;
    let t23;
    if ($[98] !== t) {
      t23 = t("Back to");
      $[98] = t;
      $[99] = t23;
    } else {
      t23 = $[99];
    }
    let t24;
    if ($[100] !== t22 || $[101] !== t23) {
      t24 = <Link href={t22} className="text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">← {t23}</Link>;
      $[100] = t22;
      $[101] = t23;
      $[102] = t24;
    } else {
      t24 = $[102];
    }
    let t25;
    if ($[103] !== hash || $[104] !== run.has_replay || $[105] !== t) {
      t25 = run.has_replay && <Link href={`/runs/${hash}/replay`} className="text-xs px-3 py-1.5 rounded-lg border border-accent text-accent hover:bg-accent/10 transition-colors">{t("Replay")}</Link>;
      $[103] = hash;
      $[104] = run.has_replay;
      $[105] = t;
      $[106] = t25;
    } else {
      t25 = $[106];
    }
    let t26;
    if ($[107] !== run.hidden || $[108] !== setHidden || $[109] !== t || $[110] !== unhiding || $[111] !== user?.is_admin) {
      t26 = user?.is_admin && <button onClick={() => setHidden(!run.hidden)} disabled={unhiding} className="text-xs px-3 py-1.5 rounded-lg border border-[var(--accent-gold)]/40 text-[var(--accent-gold)] hover:border-[var(--accent-gold)] transition-colors disabled:opacity-50">{unhiding ? "..." : run.hidden ? t("Unhide") : t("Hide")}</button>;
      $[107] = run.hidden;
      $[108] = setHidden;
      $[109] = t;
      $[110] = unhiding;
      $[111] = user?.is_admin;
      $[112] = t26;
    } else {
      t26 = $[112];
    }
    let t27;
    if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
      t27 = () => setShowReport(true);
      $[113] = t27;
    } else {
      t27 = $[113];
    }
    let t28;
    if ($[114] !== t) {
      t28 = t("Report");
      $[114] = t;
      $[115] = t28;
    } else {
      t28 = $[115];
    }
    let t29;
    if ($[116] !== t28) {
      t29 = <button onClick={t27} className="text-xs px-3 py-1.5 rounded-lg border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-accent)] transition-colors">{t28}</button>;
      $[116] = t28;
      $[117] = t29;
    } else {
      t29 = $[117];
    }
    let t30;
    if ($[118] !== copied || $[119] !== t) {
      t30 = copied ? t("Copied!") : t("Share");
      $[118] = copied;
      $[119] = t;
      $[120] = t30;
    } else {
      t30 = $[120];
    }
    let t31;
    if ($[121] !== t30) {
      t31 = <button onClick={copyLink} className="text-xs px-3 py-1.5 rounded-lg border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-accent)] transition-colors">{t30}</button>;
      $[121] = t30;
      $[122] = t31;
    } else {
      t31 = $[122];
    }
    let t32;
    if ($[123] !== t25 || $[124] !== t26 || $[125] !== t29 || $[126] !== t31) {
      t32 = <div className="flex items-center gap-2">{t25}{t26}{t29}{t31}</div>;
      $[123] = t25;
      $[124] = t26;
      $[125] = t29;
      $[126] = t31;
      $[127] = t32;
    } else {
      t32 = $[127];
    }
    let t33;
    if ($[128] !== t24 || $[129] !== t32) {
      t33 = <div className="flex items-center justify-between mb-4">{t24}{t32}</div>;
      $[128] = t24;
      $[129] = t32;
      $[130] = t33;
    } else {
      t33 = $[130];
    }
    let t34;
    if ($[131] !== emailOk || $[132] !== reportEmail || $[133] !== reportError || $[134] !== reportReason || $[135] !== reportState || $[136] !== showReport || $[137] !== submitReport || $[138] !== t) {
      t34 = showReport && <div className="fixed inset-0 z-50 bg-scrim/60 flex items-center justify-center p-4" onClick={() => reportState !== "sending" && setShowReport(false)}><div className="bg-[var(--bg-card)] border border-[var(--border-subtle)] rounded-xl p-5 w-full max-w-md" onClick={_temp4}><h2 className="text-lg font-bold text-[var(--text-primary)] mb-3">{t("Report run")}</h2>{reportState === "sent" ? <p className="text-sm text-[var(--color-silent)] py-4">{t("Report sent. Thanks!")}</p> : <div className="space-y-3"><label className="block"><span className="text-xs text-[var(--text-muted)]">{t("Why are you reporting this run?")}</span><textarea value={reportReason} onChange={e_1 => setReportReason(e_1.target.value)} rows={4} maxLength={2000} className="mt-1 w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--border-accent)] focus:outline-none" /></label><label className="block"><span className="text-xs text-[var(--text-muted)]">{t("Email")}</span><input type="email" value={reportEmail} onChange={e_2 => setReportEmail(e_2.target.value)} required={true} maxLength={254} className="mt-1 w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--border-accent)] focus:outline-none" />{reportEmail.trim() !== "" && !emailOk && <span className="text-xs text-[var(--color-ironclad)]">{t("Enter a valid email address.")}</span>}</label>{reportState === "error" && <p className="text-xs text-[var(--color-ironclad)]">{reportError || t("Could not send the report.")}</p>}<div className="flex justify-end gap-2 pt-1"><button onClick={() => setShowReport(false)} className="text-xs px-3 py-1.5 rounded-lg border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">{t("Cancel")}</button><button onClick={submitReport} disabled={reportState === "sending" || !emailOk || !reportReason.trim()} className="text-xs px-3 py-1.5 rounded-lg border border-[var(--accent-gold)]/40 bg-[var(--accent-gold)]/10 text-[var(--accent-gold)] hover:border-[var(--accent-gold)] transition-colors disabled:opacity-50">{reportState === "sending" ? "..." : t("Send report")}</button></div></div>}</div></div>;
      $[131] = emailOk;
      $[132] = reportEmail;
      $[133] = reportError;
      $[134] = reportReason;
      $[135] = reportState;
      $[136] = showReport;
      $[137] = submitReport;
      $[138] = t;
      $[139] = t34;
    } else {
      t34 = $[139];
    }
    let t35;
    if ($[140] !== run.hidden || $[141] !== t) {
      t35 = run.hidden && <div className="mb-4 rounded-lg border border-[var(--accent-gold)]/40 bg-[var(--accent-gold)]/10 px-4 py-3 text-sm text-[var(--text-secondary)]">{t("This run doesn't count toward leaderboards or community stats.")}{" "}<a href="https://discord.gg/xMsTBeh" target="_blank" rel="noopener noreferrer" className="text-[var(--accent-gold)] hover:underline">{t("Reach out on Discord if that seems wrong.")}</a></div>;
      $[140] = run.hidden;
      $[141] = t;
      $[142] = t35;
    } else {
      t35 = $[142];
    }
    const t36 = `color-mix(in srgb, ${charColor} 40%, transparent)`;
    const t37 = `color-mix(in srgb, ${charColor} 8%, var(--bg-card))`;
    let t38;
    if ($[143] !== t36 || $[144] !== t37) {
      t38 = {
        borderColor: t36,
        background: t37
      };
      $[143] = t36;
      $[144] = t37;
      $[145] = t38;
    } else {
      t38 = $[145];
    }
    const t39 = run.win ? "var(--color-silent)" : run.was_abandoned ? "var(--text-muted)" : "var(--color-ironclad)";
    let t40;
    if ($[146] !== t39) {
      t40 = {
        color: t39
      };
      $[146] = t39;
      $[147] = t40;
    } else {
      t40 = $[147];
    }
    let t41;
    if ($[148] !== run.was_abandoned || $[149] !== run.win || $[150] !== t) {
      t41 = run.win ? t("Victory") : run.was_abandoned ? t("Abandoned") : t("Defeat");
      $[148] = run.was_abandoned;
      $[149] = run.win;
      $[150] = t;
      $[151] = t41;
    } else {
      t41 = $[151];
    }
    let t42;
    if ($[152] !== t40 || $[153] !== t41) {
      t42 = <span style={t40}>{t41}</span>;
      $[152] = t40;
      $[153] = t41;
      $[154] = t42;
    } else {
      t42 = $[154];
    }
    let t43;
    if ($[155] !== bp || $[156] !== charColor || $[157] !== charId || $[158] !== charLinked || $[159] !== charName) {
      t43 = charLinked ? <Link href={`${bp}/characters/${charId.toLowerCase()}`} className="text-base font-normal hover:underline" style={{
        color: charColor
      }}>{charName}</Link> : <span className="text-base font-normal" style={{
        color: charColor
      }}>{charName}</span>;
      $[155] = bp;
      $[156] = charColor;
      $[157] = charId;
      $[158] = charLinked;
      $[159] = charName;
      $[160] = t43;
    } else {
      t43 = $[160];
    }
    let t44;
    if ($[161] !== t42 || $[162] !== t43) {
      t44 = <h1 className="flex items-center gap-3 text-xl font-bold">{t42}{t43}</h1>;
      $[161] = t42;
      $[162] = t43;
      $[163] = t44;
    } else {
      t44 = $[163];
    }
    let t45;
    if ($[164] !== t) {
      t45 = t("Ascension");
      $[164] = t;
      $[165] = t45;
    } else {
      t45 = $[165];
    }
    const t46 = run.ascension || 0;
    let t47;
    if ($[166] !== killedBy || $[167] !== t) {
      t47 = killedBy && <>{" \xB7 "}{t("Killed by")}{" "}<Link href={killedBy.href} className="hover:underline" style={{
          color: "var(--color-ironclad)"
        }}>{killedBy.name}</Link></>;
      $[166] = killedBy;
      $[167] = t;
      $[168] = t47;
    } else {
      t47 = $[168];
    }
    let t48;
    if ($[169] !== t45 || $[170] !== t46 || $[171] !== t47) {
      t48 = <div className="text-sm text-[var(--text-muted)]">{t45} {t46}{t47}</div>;
      $[169] = t45;
      $[170] = t46;
      $[171] = t47;
      $[172] = t48;
    } else {
      t48 = $[172];
    }
    let t49;
    if ($[173] !== t38 || $[174] !== t44 || $[175] !== t48) {
      t49 = <div className="rounded-xl border px-4 py-3 mb-4 flex items-center justify-between flex-wrap gap-2" style={t38}>{t44}{t48}</div>;
      $[173] = t38;
      $[174] = t44;
      $[175] = t48;
      $[176] = t49;
    } else {
      t49 = $[176];
    }
    const t50 = killedBy?.name;
    let t51;
    if ($[177] !== bp || $[178] !== cardData || $[179] !== charColor || $[180] !== charLinked || $[181] !== charName || $[182] !== player || $[183] !== potionData || $[184] !== relicData || $[185] !== run || $[186] !== t50) {
      t51 = <RunSummary run={run} player={player} cardData={cardData} relicData={relicData} potionData={potionData} charColor={charColor} charLinked={charLinked} langPrefix={bp} charName={charName} killedByName={t50} />;
      $[177] = bp;
      $[178] = cardData;
      $[179] = charColor;
      $[180] = charLinked;
      $[181] = charName;
      $[182] = player;
      $[183] = potionData;
      $[184] = relicData;
      $[185] = run;
      $[186] = t50;
      $[187] = t51;
    } else {
      t51 = $[187];
    }
    let t52;
    if ($[188] === Symbol.for("react.memo_cache_sentinel")) {
      t52 = () => setShowDetails(_temp5);
      $[188] = t52;
    } else {
      t52 = $[188];
    }
    const t53 = `inline-block transition-transform ${showDetails ? "rotate-90" : ""}`;
    let t54;
    if ($[189] !== t53) {
      t54 = <span className={t53}>{">"}</span>;
      $[189] = t53;
      $[190] = t54;
    } else {
      t54 = $[190];
    }
    let t55;
    if ($[191] !== showDetails || $[192] !== t) {
      t55 = showDetails ? t("Hide") : t("Show");
      $[191] = showDetails;
      $[192] = t;
      $[193] = t55;
    } else {
      t55 = $[193];
    }
    let t56;
    if ($[194] !== t) {
      t56 = t("detailed history");
      $[194] = t;
      $[195] = t56;
    } else {
      t56 = $[195];
    }
    let t57;
    if ($[196] !== t54 || $[197] !== t55 || $[198] !== t56) {
      t57 = <button onClick={t52} className="w-full text-left text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors mb-3 flex items-center gap-2">{t54}{t55} {t56}</button>;
      $[196] = t54;
      $[197] = t55;
      $[198] = t56;
      $[199] = t57;
    } else {
      t57 = $[199];
    }
    t21 = <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{t33}{t34}{t35}{t49}{t51}{t57}{showDetails && <><div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-subtle)] p-5 mb-4"><h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">{t("Final Deck")} ({player.deck.length})</h2><div className="flex flex-wrap gap-1.5">{sortedDeck.map((card, i) => <CardPill key={`${card.id}-${i}`} cardId={card.id} upgraded={!!card.current_upgrade_level} enchantment={card.enchantment?.id} cardData={cardData} bp={bp} className={`text-xs px-2 py-1 rounded border transition-colors hover:bg-[var(--bg-card-hover)] ${card.current_upgrade_level ? "border-[var(--color-silent)]/30 bg-[var(--color-silent)]/10 text-[var(--color-silent)]" : "bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-secondary)]"}`} />)}</div></div><div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-subtle)] p-5 mb-4"><h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">{t("Relics")} ({player.relics.length})</h2><div className="flex flex-wrap gap-1.5">{player.relics.map((relic, i_0) => <RelicPill key={`${relic.id}-${i_0}`} relicId={relic.id} relicData={relicData} bp={bp} className="text-xs px-2 py-1 rounded bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--accent-gold)] hover:bg-[var(--bg-card-hover)] transition-colors">{relicTitle(relic.id)}<span className="text-[var(--text-muted)] ml-1">F{relic.floor_added_to_deck}</span></RelicPill>)}</div></div><div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-subtle)] p-5"><h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">{t("Floor History")}</h2><div className="space-y-1">{run.floor_history.map((actFloors, actIdx) => {
              const actId = run.acts[actIdx];
              const actName = (actId ? gt(`acts.${actId}.title`) : undefined) ?? t("Act {n}", {
                n: actIdx + 1
              });
              return <div key={actIdx}><h3 className="text-sm font-semibold text-[var(--text-muted)] uppercase tracking-wider mt-3 mb-1.5">{actName}</h3>{actFloors.map((floor, floorIdx) => {
                  const ps = floor.player_stats[0];
                  const room = typedRoom(floor);
                  const picked = ps?.card_choices?.filter(_temp6).map(c_2 => cardTitle(c_2.card.id)) || [];
                  const skipped = ps?.card_choices?.filter(_temp7).map(c_4 => cardTitle(c_4.card.id)) || [];
                  const rested = ps?.rest_site_choices?.map(c_5 => restChoiceName(c_5, gt, t)) || [];
                  const upgraded = ps?.upgraded_cards?.map(id_3 => cardTitle(id_3)) || [];
                  const chose = ps?.event_choices?.map(c_6 => eventChoiceName(c_6, gt)).filter(_temp8) || [];
                  return <div key={floorIdx} className="flex items-start gap-3 py-1.5 border-b border-[var(--border-subtle)] last:border-0 text-xs"><span className="text-[var(--text-muted)] w-6 text-right flex-shrink-0">{floorIdx + 1}</span><span className="w-14 flex-shrink-0 font-medium" style={{
                      color: ROOM_COLORS[floor.raw_type] || "var(--text-secondary)"
                    }}>{roomTypeTitle(floor, gt, t)}</span><div className="flex-1 min-w-0"><span className="text-[var(--text-secondary)]">{roomTitle(floor, gt)}</span>{room?.type === "ENCOUNTER" && room.turns_taken != null && <span className="text-[var(--text-muted)] ml-1">({room.turns_taken}T)</span>}{rested.length > 0 && <span className="text-[var(--text-secondary)] ml-1">{rested.join(", ")}</span>}{chose.length > 0 && <span className="text-[var(--text-secondary)] ml-1 italic">{t("chose {choice}", {
                          choice: chose.join(", ")
                        })}</span>}{picked.length > 0 && <span className="ml-2" style={{
                        color: "var(--color-silent)"
                      }}>+{picked.join(", ")}</span>}{skipped.length > 0 && <span className="text-[var(--text-muted)] ml-1 line-through">{skipped.join(", ")}</span>}{upgraded.length > 0 && <span className="ml-2" style={{
                        color: "var(--accent-gold)"
                      }}>⬆ {upgraded.join(", ")}</span>}</div>{ps && <div className="flex items-center gap-2 flex-shrink-0 text-[var(--text-muted)]">{(ps.damage_taken ?? 0) > 0 && <span style={{
                        color: "var(--color-ironclad)"
                      }}>-{ps.damage_taken}</span>}{(ps.hp_healed ?? 0) > 0 && <span style={{
                        color: "var(--color-silent)"
                      }}>+{ps.hp_healed}</span>}<span>{ps.current_hp}/{ps.max_hp}</span></div>}</div>;
                })}</div>;
            })}</div></div><SimilarRuns hash={hash} /></>}</div>;
    $[69] = bp;
    $[70] = cardData;
    $[71] = cardTitle;
    $[72] = charColor;
    $[73] = charId;
    $[74] = charLinked;
    $[75] = charName;
    $[76] = copied;
    $[77] = emailOk;
    $[78] = gt;
    $[79] = hash;
    $[80] = killedBy;
    $[81] = player;
    $[82] = potionData;
    $[83] = relicData;
    $[84] = relicTitle;
    $[85] = reportEmail;
    $[86] = reportError;
    $[87] = reportReason;
    $[88] = reportState;
    $[89] = run;
    $[90] = setHidden;
    $[91] = showDetails;
    $[92] = showReport;
    $[93] = submitReport;
    $[94] = t;
    $[95] = unhiding;
    $[96] = user?.is_admin;
    $[97] = t21;
  } else {
    t21 = $[97];
  }
  return t21;
}
function _temp8(name) {
  return !!name;
}
function _temp7(c_3) {
  return !c_3.was_picked;
}
function _temp6(c_1) {
  return c_1.was_picked;
}
function _temp5(v) {
  return !v;
}
function _temp4(e_0) {
  return e_0.stopPropagation();
}
function _temp3(a, b) {
  return a.id.localeCompare(b.id);
}
function _temp2() {
  return null;
}
function _temp(r) {
  if (!r.ok) {
    throw new Error();
  }
  return r.json();
}